// For better TypeScript integration, consider installing type definitions:
// `npm install --save-dev @types/swiper @types/gsap @types/scrolltrigger`
// and then importing them:
// import Swiper from 'swiper';
// import { gsap } from 'gsap';
// import { ScrollTrigger } from 'gsap/ScrollTrigger';
declare var gsap: any;
declare var Splide: any;
declare var ScrollTrigger: any;
declare var dataLayer: any[];
/**
 * サイト全体の共通機能を管理するクラス
 */
class Common {
    // --- 定数定義 ---
    private static readonly SCROLL_OFFSET = 140;
    private static readonly SCROLL_CLASS_THRESHOLD = 100;
    private static readonly READ_MORE_INITIAL_HEIGHT = '220px';
    private static readonly PAGE_LOAD_TIMEOUT = 10000;

    private body: HTMLElement;
    private hamburger: HTMLElement | null;
    private opElement: HTMLElement | null;
    private header: HTMLElement | null;
    private pageLoadTimeoutId: number | undefined;
    private pageLoadCompleted: boolean = false; // ページロード完了処理が実行されたかどうかのフラグ

    constructor() {
        this.body = document.body;
        this.hamburger = document.querySelector('.hamburger');
        this.opElement = document.querySelector<HTMLElement>('.op');
        this.header = document.querySelector<HTMLElement>('#header'); // ヘッダー要素を取得
    }

    /**
     * 全ての共通機能を初期化する
     */
    init() {
        this.setupDeviceClass();
        this.setupHamburgerMenu();
        this.setupScrollAnimations(); // 先にアニメーションをセットアップ
        this.setupSmoothScroll();
        this.setupScrollClass();
        this.setupProductSlider();
        this.setupReadMore();
        this.setupTextSplit();
        this.setupPageLoadComplete();
    }

    setupDeviceClass() {
        const userAgent = navigator.userAgent.toLowerCase();
        const isIPad = userAgent.includes('ipad') ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        this.body.classList.toggle('ipad', isIPad);
    }

    /**
     * ハンバーガーメニューの開閉機能をセットアップする
     */
    setupHamburgerMenu() {
        if (!this.hamburger) return;

        this.hamburger.addEventListener('click', () => {
            this.body.classList.toggle('nav-open');
        });
    }

    // スクロールアニメーションの設定
    setupScrollAnimations() {
        // 既存のScrollTriggerインスタンスをクリア
        ScrollTrigger.getAll().forEach(trigger => trigger.kill());

        gsap.utils.toArray<HTMLElement>('.framein').forEach((framein) => {
            // .bg4の子孫要素であるかどうかを判定
            const isInsideBg4 = framein.closest('.bg4');

            ScrollTrigger.create({
                trigger: framein,
                start: "top 80%", // 要素の下部がビューポートに入ったらアニメーションを開始
                toggleClass: "in", // クラスをトグル
                once: true, // トリガー発火後にインスタンスを破棄し、クラスが付与された状態を維持
                markers: false, // デバッグ用のマーカーを非表示
                // .bg4の外にある.framein要素にのみ、refreshPriorityとinvalidateOnRefreshを適用
                // これにより、.bg4のpinアニメーションによるレイアウト変更後に正しく位置を再計算する
                refreshPriority: isInsideBg4 ? undefined : -1,
                invalidateOnRefresh: isInsideBg4 ? false : true,
            });
        });

        // .bg3の背景エフェクト
        const bg3Effect = document.querySelector<HTMLElement>('.bg3 .bg-effect');
        const contents4Inner = document.querySelector<HTMLElement>('#contents4 .inner');
        if (bg3Effect && contents4Inner) {
            // 初期状態を設定
            gsap.set(contents4Inner, { opacity: 0 });

            // アニメーションを設定
            gsap.to([bg3Effect, contents4Inner], {
                duration: 1.0, 
                opacity: 1,
                scrollTrigger: {
                    trigger: '#contents4',
                    start: 'top 30%', // #contents4の上辺がビューポートの半分に達した時に開始
                    toggleActions: "play none none reverse", // 表示されたら再生、スクロールして戻ったら逆再生
                    markers: false    // デバッグ用
                }
            });
        }

        // .bg4のアニメーション（matchMediaを使用してレスポンシブ対応）
        ScrollTrigger.matchMedia({
            // PCサイズ (928px以上)
            // is-spクラスを削除
            "(min-width: 928px) and (max-width: 10000px)": () => {
                const bg4 = document.querySelector<HTMLElement>('.bg4');
                if (bg4) {
                    bg4.classList.remove('is-sp');
                }
            },
            "(min-width: 928px)": () => {
                const bg4 = document.querySelector<HTMLElement>('.bg4');
                if (bg4) {
                    const bg4Effect = bg4.querySelector<HTMLElement>('.bg-effect');
                    const texts = gsap.utils.toArray<HTMLElement>('.bg4 .text-wrap .text-fade-up');
                    const tl = gsap.timeline({
                        scrollTrigger: {
                            trigger: bg4,
                            start: 'top top', // .bg4の上辺が画面の上辺に来たら開始
                            markers: false,
                            once: true // アニメーションを1回だけ実行
                        }
                    });

                    if (bg4Effect) {
                        tl.to(bg4Effect, {
                            autoAlpha: 0
                        });
                    }

                    texts.forEach((text) => {
                        tl.from(text, {
                            autoAlpha: 0,
                            y: 50,
                            filter: 'blur(8px)', // ボケた状態から戻るアニメーションを追加
                        });
                    });

                    // クリーンアップ関数
                    return () => {
                        // GSAPによって追加されたインラインスタイルをクリア
                        gsap.set(texts, { clearProps: "all" });
                        // タイムラインとScrollTriggerを破棄
                        if (tl) tl.kill();
                    };
                }
            },
            // スマホサイズ (927px以下)
            "(max-width: 927px)": () => {
                const bg4 = document.querySelector<HTMLElement>('.bg4');
                if (bg4) {
                    bg4.classList.add('is-sp');
                }
                const texts = gsap.utils.toArray<HTMLElement>('.bg4 .text-wrap .text-fade-up');
                const triggers: ScrollTrigger[] = [];

                texts.forEach((text) => {
                    // .framein と同じクラスを付与して共通のスタイルを適用
                    text.classList.add('framein', 'scr-fade-up');
                    const st = ScrollTrigger.create({
                        trigger: text,
                        start: "top 80%",
                        toggleClass: "in",
                        once: true
                    });
                    triggers.push(st);
                });

                // クリーンアップ関数
                return () => {
                    texts.forEach(text => {
                        text.classList.remove('framein', 'scr-fade-up', 'in');
                    });
                    triggers.forEach(st => st.kill());
                    if (bg4) {
                        bg4.classList.remove('is-sp');
                    }
                };
            }
        });
    }

    // .product-sliderの設定
    setupProductSlider() {
        const sliderContainer = document.querySelector<HTMLElement>('.product-slider');
        // スライダーコンテナと、スライドが1枚以上存在することを確認してから処理を続行
        if (!sliderContainer || sliderContainer.querySelectorAll('.slider-item').length === 0) {
            console.warn('Splide container or slides not found. Slider will not be initialized.');
            return;
        }

        const splide = new Splide(sliderContainer, {
            type: 'loop',
            focus: 'center',
            autoWidth: true,
            gap: '100px',
            pagination: false, // HTMLに記述するため無効化
            arrows: true, // HTMLに記述した矢印を有効化
            snap: true,
            // drag: "free",
            drag: false,
            rewindByDrag: false,
            breakpoints: {
                927: { // sp
                    gap: '20px',
                    autoWidth: false,
                },
            }
        });

        const pageText = sliderContainer.querySelector<HTMLElement>('.slider-pagination');

        const updatePagination = (index: number) => {
            if (pageText) {
                const realIndex = index % splide.length;
                pageText.textContent = `${realIndex + 1} / ${splide.length}`;
            }
        };

/**
 * スライドの状態（ページネーションとURLハッシュ）を更新する
 * @param index - 更新対象のスライドのインデックス
 */
const updateSlideState = (index: number) => {
    const slides = splide.Components.Slides.get();
    const hash = slides.find(s => s.index === index)?.slide.getAttribute('hash');

    if (hash) {
        // カノニカルURLの要素を取得してパスを取り出す
        const canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        const canonicalPath = canonicalLink ? new URL(canonicalLink.href).pathname : window.location.pathname;
        
        // カノニカルパス + ハッシュ を組み合わせたURL
        const targetUrl = canonicalPath + hash;

        // 現在の「パス + ハッシュ」と異なる場合のみURLを置き換える
        const currentUrl = window.location.pathname + window.location.hash;
        if (currentUrl !== targetUrl) {
            history.replaceState(null, '', targetUrl);
        }
    }
    
    updatePagination(index);
};

        // スライダーがマウントされた後に処理を開始
        splide.on('mounted', function () {
            const hash = window.location.hash;

            if (hash) {
                const slides = splide.Components.Slides.get().map(s => s.slide);
                const targetIndex = slides.findIndex(slide => slide.getAttribute('hash') === hash);

                if (targetIndex !== -1) {
                    splide.go(targetIndex);
                    // splide.goは'move'イベントを発火させるため、ここでのupdatePagination呼び出しは不要
                    return;
                }
            }

            // ハッシュがない場合、または一致するスライドがない場合の初期ページネーション更新
            updatePagination(splide.index);
        });

        // スライド移動完了後に発火
        splide.on('moved', (newIndex) => {
            updateSlideState(newIndex);
            const activeSlide = splide.Components.Slides.getAt(newIndex)?.slide;
            if (activeSlide) {
                const hash = activeSlide.getAttribute('hash');
                const pageVirtual = activeSlide.getAttribute('pageVirtual');
                if (hash && pageVirtual) {
                    const canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
                    const canonicalUrl = canonicalLink ? new URL(canonicalLink.href).pathname : '';
                    // dataLayerに仮想PVイベントを送信
                    dataLayer.push({
                        'event': 'articleVirtualPV',
                        'VPVpath': canonicalUrl + hash,
                        'pageVirtual': pageVirtual
                    });
                }
            }
        });


        // ドラッグ操作でのスクロール時に発火
        splide.on('scrolled', () => {
            // is-activeクラスを持つオリジナルのスライド（クローンではない）を取得
            const activeOriginalSlide = sliderContainer.querySelector<HTMLElement>('.splide__slide:not(.splide__slide--clone).is-active');

            if (activeOriginalSlide) {
                // is-activeなスライドが中央にあるとみなし、そのインデックスを取得
                // Splideの内部データ属性からインデックスを取得
                const activeIndex = parseInt(activeOriginalSlide.getAttribute('aria-roledescription')?.split('/')[0] || '0', 10) - 1;
                if (activeIndex >= 0) {
                    // `move`イベントと重複しないように、現在のインデックスと異なる場合のみ実行
                    if (splide.index !== activeIndex) {
                        updateSlideState(activeIndex);
                        const hash = activeOriginalSlide.getAttribute('hash');
                        const pageVirtual = activeOriginalSlide.getAttribute('pageVirtual');
                        if (hash && pageVirtual) {
                            const canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
                            const canonicalUrl = canonicalLink ? new URL(canonicalLink.href).pathname : '';
                            // dataLayerに仮想PVイベントを送信
                            dataLayer.push({
                                'event': 'articleVirtualPV',
                                'VPVpath': canonicalUrl + hash,
                                'pageVirtual': pageVirtual
                            });
                        }
                    }
                    return; // is-activeなスライドが見つかった場合はここで処理を終了
                }
            }

            // フォールバック：is-activeなスライドが見つからない場合（稀なケース）
            // 中央に最も近いスライドを計算する
            const originalSlides = splide.Components.Slides.get().filter(slide => !slide.isClone);
            const track = sliderContainer.querySelector<HTMLElement>('.splide__track');
            if (!track || originalSlides.length === 0) return;

            const viewportCenter = sliderContainer.getBoundingClientRect().width / 2;
            let minDistance = Infinity;
            let closestIndex = -1;

            originalSlides.forEach(slide => {
                const slideRect = slide.slide.getBoundingClientRect();
                const slideCenter = slideRect.left - track.getBoundingClientRect().left + slideRect.width / 2;
                const distance = Math.abs(viewportCenter - slideCenter);
                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = slide.index;
                }
            });

            if (closestIndex !== -1) {
                updateSlideState(closestIndex);
            }
        });

        splide.mount();
    }

    // スムーススクロールの設定
    setupSmoothScroll() {
        const navLinks = document.querySelectorAll<HTMLAnchorElement>("nav a[href^='#']");

        navLinks.forEach((navLink) => {
            navLink.addEventListener("click", (e) => {
                e.preventDefault();

                const href = navLink.getAttribute('href');
                if (!href) return;

                this.body.classList.remove('nav-open');

                // ScrollToPluginを使用してスムーススクロール
                // これにより、ScrollTrigger.refresh()が実行されてもスクロールが中断されない
                gsap.to(window, {
                    duration: 1.5, // アニメーションの時間（秒）
                    scrollTo: {
                        y: href,
                        offsetY: 0, // ターゲットが画面の上辺に来るようにオフセットを0に設定
                    },
                    ease: 'power2.inOut', // イージング
                });
            });
        });
    }

    // スクロールでクラスを付与
    setupScrollClass() {
        let ticking = false;

        const updateScrollClass = () => {
            if (window.scrollY > Common.SCROLL_CLASS_THRESHOLD) {
                this.body.classList.add('is-scroll');
            } else {
                this.body.classList.remove('is-scroll');
            }
            ticking = false;
        };

        window.addEventListener('scroll', () => {
            if (!ticking) {
                window.requestAnimationFrame(updateScrollClass);
                ticking = true;
            }
        });
    }

    // 「もっと見る」機能の設定
    setupReadMore() {
        const moreElements = document.querySelectorAll<HTMLElement>('.more');

        moreElements.forEach(more => {
            const btn = more.querySelector<HTMLElement>('.more-btn');
            const body = more.querySelector<HTMLElement>('.more-body');

            if (btn && body) {
                // 初期高さを設定
                body.style.maxHeight = Common.READ_MORE_INITIAL_HEIGHT;

                btn.addEventListener('click', () => {
                    more.classList.toggle('active');

                    if (more.classList.contains('active')) {
                        body.style.maxHeight = `${body.scrollHeight}px`;
                    } else {
                        body.style.maxHeight = Common.READ_MORE_INITIAL_HEIGHT;
                    }
                });
            }
        });
    }

    // ページ読み込み完了時の処理
    setupPageLoadComplete() {
        // ページ読み込み時はスクロールアニメーションを無効化
        ScrollTrigger.disable(true);
        
        if (this.opElement) {
            // .op要素がある場合のみ、オープニングアニメーション関連の処理を実行
            // ページ読み込み時にトップにスクロールするのをやめて、OPアニメーション後に実行
            history.scrollRestoration = 'manual';

            // OPアニメーション中はスクロールを禁止し、#headerを固定
            this.body.style.overflow = 'hidden';
            if (this.header) {
                this.header.classList.add('is-fixed-on-load');
            }

            // OPアニメーション終了時、またはタイムアウト時に処理を実行
            this.opElement.addEventListener('transitionend', this._completePageLoadAnimation.bind(this), { once: true });

            // タイムアウトを設定し、指定時間経過後に完了処理を実行（フォールバック）
            this.pageLoadTimeoutId = window.setTimeout(() => {
                this._completePageLoadAnimation();
            }, Common.PAGE_LOAD_TIMEOUT);
        }

        // window.loadイベントでも_completePageLoadAnimationを呼び出す
        // これにより、transitionendが発火しない場合（例: 画像が重いなど）でも処理が実行される
        window.addEventListener('load', this._completePageLoadAnimation.bind(this), { once: true });
    }

    /**
     * OPアニメーション完了時、またはタイムアウト時に実行される処理
     * スクロール有効化、ヘッダー固定解除、スクロール位置調整などを行う
     */
    private _completePageLoadAnimation() {
        if (this.pageLoadCompleted) { // 既に完了処理が実行されている場合は何もしない
            return;
        }
        this.pageLoadCompleted = true; // 完了フラグをセット

        // タイムアウトが設定されていればクリア
        if (this.pageLoadTimeoutId !== undefined) {
            clearTimeout(this.pageLoadTimeoutId);
            this.pageLoadTimeoutId = undefined; // 次の呼び出しのためにリセット
        }

        // .op要素がある場合のみ、アニメーション完了後の後処理を実行
        if (this.opElement) {
            // #headerの固定を解除
            if (this.header) {
                this.header.classList.remove('is-fixed-on-load');
            }

            // ページトップへスクロール
            window.scrollTo(0, 0);

            // bodyのスクロール禁止を解除
            this.body.style.overflow = '';
        }
        // bodyにcompleteクラスを付与
        this.body.classList.add('complete');

        // ScrollTriggerアニメーションを有効化
        ScrollTrigger.enable(true);
    }
    /**
     * 対象要素のテキストを1文字ずつ分割してspanで囲む
     * 対象: .scr-txt-split
     */
    setupTextSplit() {
        const splitTargets = document.querySelectorAll<HTMLElement>('.scr-txt-split');

        splitTargets.forEach(target => {
            const originalHTML = target.innerHTML;
            const textContent = originalHTML.replace(/<br\s*\/?>/gi, '\n');

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = textContent;
            const text = tempDiv.textContent || '';

            target.innerHTML = ''; // 元のコンテンツをクリア

            let charIndex = 0;
            for (const char of text) {
                if (char === '\n') {
                    target.appendChild(document.createElement('br'));
                } else {
                    const span = document.createElement('span');
                    span.textContent = char;
                    span.style.setProperty('--char-index', String(charIndex++));
                    target.appendChild(span);
                }
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const common = new Common();
    common.init();
});
